import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { waLink } from "@/lib/api";
import { Copy, FileCheck2, HandCoins, MessageCircle, PenLine, Users } from "lucide-react";
import { toast } from "sonner";

const CF = "14869890963";

const STEPS = [
  {
    icon: FileCheck2,
    title: "Compila la dichiarazione dei redditi",
    text: "Vale per tutti i modelli: 730, Modello Redditi o Certificazione Unica. Serve anche a chi ha solo la pensione.",
  },
  {
    icon: PenLine,
    title: "Firma il riquadro dell'8 per mille",
    text: "Basta una firma nell'apposito spazio. Se non firmi, quella quota viene comunque trattenuta: tanto vale scegliere dove va.",
  },
  {
    icon: Users,
    title: "Indica l'Associazione Vittime del Debito",
    text: "Riporta il codice fiscale 14869890963: così i fondi arrivano a chi assiste gratuitamente le persone in difficoltà.",
  },
];

export default function OttoPerMillePage() {
  return (
    <div className="container-site py-16 lg:py-24" data-testid="otto-per-mille-page">
      <Reveal className="max-w-3xl mx-auto text-center">
        <span className="eyebrow justify-center">
          <HandCoins className="w-4 h-4" />
          Sostienici con l'8 per mille
        </span>
        <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-ink">
          Una firma che non ti costa nulla
          <span className="text-brand-aqua"> e aiuta chi sta peggio.</span>
        </h1>
      </Reveal>

      <Reveal delay={0.1} className="max-w-3xl mx-auto mt-10">
        <div className="rounded-3xl bg-brand-blue text-white p-8 sm:p-12 text-center shadow-xl shadow-brand-blue/20" data-testid="otto-per-mille-message">
          <p className="text-xl sm:text-2xl font-bold leading-relaxed">
            "Destinando il tuo 8 per mille all'Associazione Vittime del Debito non spendi nulla in
            più, ma ci permetti di aiutare gratuitamente più persone in difficoltà.{" "}
            <span className="text-brand-aqua">Codice fiscale: 14869890963.</span> Basta una firma
            nella tua dichiarazione dei redditi."
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15} className="max-w-3xl mx-auto mt-8">
        <div className="rounded-2xl bg-white border-2 border-brand-aqua/30 px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="otto-per-mille-cf-box">
          <div className="text-center sm:text-left">
            <p className="text-sm font-bold uppercase tracking-wider text-slate-500">Codice fiscale da indicare</p>
            <p className="font-extrabold text-brand-blue text-3xl tracking-wide" data-testid="otto-per-mille-cf">{CF}</p>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(CF); toast.success("Codice fiscale copiato"); }}
            className="btn-outline shrink-0"
            data-testid="otto-per-mille-copy-button"
          >
            <Copy className="w-5 h-5" />
            Copia il codice
          </button>
        </div>
      </Reveal>

      <Reveal delay={0.2} className="max-w-3xl mx-auto mt-12">
        <h2 className="text-2xl font-extrabold text-brand-ink text-center">Come fare, in 3 passi</h2>
        <div className="mt-6 space-y-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="flex gap-5 items-start bg-white rounded-3xl border border-brand-aqua/20 p-6 shadow-sm" data-testid={`otto-per-mille-step-${i + 1}`}>
              <span className="w-14 h-14 shrink-0 rounded-2xl bg-brand-aqua text-white flex items-center justify-center text-xl font-extrabold">
                {i + 1}
              </span>
              <div>
                <h3 className="font-bold text-lg text-brand-ink">{s.title}</h3>
                <p className="mt-1 text-slate-600 leading-relaxed">{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.25} className="max-w-3xl mx-auto mt-12">
        <div className="rounded-3xl bg-brand-mist p-8 text-center" data-testid="otto-per-mille-cta">
          <h2 className="text-xl font-extrabold text-brand-ink">Hai dubbi su come compilarla?</h2>
          <p className="mt-2 text-slate-600">
            Ti guidiamo noi, gratis. E per approfondire leggi la nostra guida dedicata.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={waLink("Buongiorno, vorrei aiuto per destinare il mio 8 per mille all'Associazione Vittime del Debito (C.F. 14869890963).")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-bold text-lg px-8 h-14 transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="otto-per-mille-whatsapp-button"
            >
              <MessageCircle className="w-5 h-5" />
              Scrivici su WhatsApp
            </a>
            <Link to="/guide/otto-per-mille-come-destinarlo" className="btn-outline" data-testid="otto-per-mille-guide-link">
              Leggi la guida completa
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
